// WYNG Welcome (1 Feb 2026)
//
// Single-screen entry: hero image + brand on top, magic-link form
// directly inline. No intermediate "Inloggen" tap needed.
//
// Demo button: visible on web/preview (non-native) builds, OR when
// `?demo=1` is in the URL. Hidden on TestFlight / native iOS prod.
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Mail, Send, Loader2, Sparkles, Check, Copy, Users, Calendar, Brain } from "lucide-react";
import { toast } from "sonner";
import { Capacitor } from "@capacitor/core";
import api from "@/api";
import { useAuth } from "@/auth/AuthContext";
import LanguagePicker from "@/components/LanguagePicker";
export default function Welcome() {
  const nav = useNavigate();
  const {
    t
  } = useTranslation();
  const [search] = useSearchParams();
  const {
    user,
    loginAsDemo
  } = useAuth();
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [devLink, setDevLink] = useState(null);
  const [demoLoading, setDemoLoading] = useState(false);

  // Capture `?ref=` URL param the moment user lands. Stored in
  // sessionStorage so MagicConsume can attach it to the JWT exchange
  // even after a round-trip through the user's email client.
  useEffect(() => {
    const ref = search.get("ref");
    if (ref && /^[A-Z0-9-]{4,32}$/i.test(ref)) {
      try {
        sessionStorage.setItem("wyng_ref_code", ref.toUpperCase());
      } catch {/* noop */}
    }
  }, [search]);

  // Demo button visibility: always when ?demo=1, and on non-native
  // (web/preview) builds. Native iOS prod hides it.
  const isNative = (() => {
    try {
      return Capacitor.isNativePlatform?.() === true;
    } catch {
      return false;
    }
  })();
  const demoVisible = search.get("demo") === "1" || !isNative;

  // Bounce away if already authed (cached refresh-token still valid).
  useEffect(() => {
    if (user && user.id) nav("/dashboard", {
      replace: true
    });
  }, [user, nav]);
  const requestLink = async e => {
    e?.preventDefault?.();
    const v = email.trim().toLowerCase();
    if (!v || !v.includes("@")) {
      toast.error(t("welcome.voer_een_geldig_e_mailadres_in"));
      return;
    }
    setSending(true);
    try {
      const {
        data
      } = await api.post("/auth/magic/request", {
        email: v
      });
      setSent(true);
      setDevLink(data?.dev_link || null);
      if (data?.dev_link) {
        toast.message(t("welcome.dev_mode_link_verschijnt_op_het_scherm"), {
          duration: 3500
        });
      } else {
        toast.success(t("welcome.check_je_inbox_klik_op_de_link_om_in_te_"));
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kon link niet versturen");
    } finally {
      setSending(false);
    }
  };
  const demo = async () => {
    setDemoLoading(true);
    try {
      const u = await loginAsDemo();
      toast.success(`Welkom terug, ${u.name || "tester"}`);
      nav("/dashboard", {
        replace: true
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Demo login mislukt");
    } finally {
      setDemoLoading(false);
    }
  };
  return <div className="app-shell min-h-[100dvh] bg-background relative overflow-hidden" data-testid="welcome-page">
            {/* Language picker top-right */}
            <div className="absolute right-4 top-4 z-20 flex gap-2">
                <LanguagePicker />
            </div>

            {/* Ambient glow accents (subtle, behind everything) */}
            <div className="absolute -top-32 -left-32 w-80 h-80 rounded-full pointer-events-none" style={{
      background: "radial-gradient(circle, rgba(255,214,10,0.18) 0%, transparent 70%)"
    }} />
            <div className="absolute -bottom-40 -right-40 w-96 h-96 rounded-full pointer-events-none" style={{
      background: "radial-gradient(circle, rgba(50,215,75,0.10) 0%, transparent 70%)"
    }} />

            {/* Hero image — kept for brand polish, shorter aspect so the
                form sits comfortably above the fold on iPhone 15. */}
            <div className="relative w-full aspect-[16/9] sm:aspect-[3/2] overflow-hidden">
                <img src="/wing-hero.png" alt={t("welcome.wyng_built_today_elevated_forever")} className="w-full h-full object-cover" style={{
        objectPosition: "center 40%"
      }} data-testid="welcome-hero-image" />
                <div className="absolute inset-x-0 bottom-0 h-28 bg-gradient-to-b from-transparent to-background" />
            </div>

            {/* Content — pulled up over the hero gradient */}
            <div className="relative px-6 pb-10 -mt-14 z-10">
                {/* Brand mark */}
                <div className="flex items-center gap-2 mb-4">
                    <img src="/wing-mark.png" alt={t("welcome.wyng")} className="h-9 w-9 rounded-full" data-testid="welcome-wing-mark" />
                    <span className="font-display text-lg font-semibold tracking-tight">
                        {t("welcome.wyng")}
                    </span>
                </div>

                {/* Headline */}
                <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tighter leading-[1.05]">
                    {t("welcome.headline_1")} <br />{t("welcome.headline_2")}
                </h1>
                <p className="text-muted-foreground mt-3 text-sm leading-relaxed">
                    {t("welcome.subtitle")}
                </p>

                {/* Social proof pills — reinforces social/coach positioning
                    over "yet another metrics app". Three feature anchors:
                    1) Challenges 2) Meet-ups 3) AI coach. */}
                <div className="mt-5 flex flex-wrap gap-2" data-testid="welcome-pills">
                    <span className="inline-flex items-center gap-1.5 rounded-full
                                     bg-foreground/5 border border-border
                                     px-3 py-1.5 text-[11.5px] font-medium">
                        <Users size={13} className="text-[#FFD60A]" />
                        {t("welcome.pill_social")}
                    </span>
                    <span className="inline-flex items-center gap-1.5 rounded-full
                                     bg-foreground/5 border border-border
                                     px-3 py-1.5 text-[11.5px] font-medium">
                        <Calendar size={13} className="text-[#32D74B]" />
                        {t("welcome.pill_meetups")}
                    </span>
                    <span className="inline-flex items-center gap-1.5 rounded-full
                                     bg-foreground/5 border border-border
                                     px-3 py-1.5 text-[11.5px] font-medium">
                        <Brain size={13} className="text-[#FF9F0A]" />
                        {t("welcome.pill_coach")}
                    </span>
                </div>

                {/* Magic-link form OR sent confirmation */}
                {!sent ? <form onSubmit={requestLink} className="mt-6 w-full" data-testid="magic-form">
                        <label className="text-[10.5px] uppercase tracking-wider font-bold text-muted-foreground mb-2 block">
                            {t("welcome.je_e_mailadres")}
                        </label>
                        <div className="relative">
                            <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                            <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder={t("welcome.naam_email_com")} autoComplete="email" data-testid="magic-email" className="w-full bg-foreground/5 border border-border rounded-2xl
                                           pl-10 pr-4 py-3.5 text-[14px] outline-none
                                           focus:border-[#FFD60A]/60 transition" />
                        </div>
                        <button type="submit" disabled={sending || !email.trim()} data-testid="magic-submit" className="mt-3 w-full bg-[#FFD60A] text-black font-bold py-3.5
                                       rounded-2xl text-[14.5px] flex items-center justify-center gap-2
                                       active:scale-[0.99] transition disabled:opacity-50">
                            {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                            {sending ? "Versturen…" : "Stuur magic link"}
                        </button>

                        <p className="mt-3 text-[11.5px] text-muted-foreground text-center leading-relaxed">
                            {t("welcome.geen_wachtwoord_we_sturen_een_1_tap_inlo")}
                            <br />{t("welcome.sessie_blijft_90_dagen_actief")}
                        </p>
                    </form> : <div className="mt-6 w-full text-center" data-testid="magic-sent">
                        <div className="h-14 w-14 mx-auto rounded-full bg-[#32D74B]/15 border border-[#32D74B]/30 flex items-center justify-center mb-3">
                            <Check size={22} className="text-[#32D74B]" />
                        </div>
                        <h2 className="font-display text-xl font-semibold mb-2">
                            {t("welcome.check_je_inbox")}
                        </h2>
                        <p className="text-[13px] text-muted-foreground leading-relaxed">
                            {t("welcome.we_hebben_een_inloglink_naar")}<br />
                            <strong className="text-foreground">{email.toLowerCase().trim()}</strong> gestuurd.
                            <br />{t("welcome.open_de_mail_en_tik_op_de_knop_om_binnen")}
                        </p>

                        {devLink && <div className="mt-4 rounded-2xl p-3 text-left" style={{
          background: "rgba(255,214,10,0.08)",
          border: "1px dashed rgba(255,214,10,0.32)"
        }}>
                                <p className="text-[10px] uppercase tracking-wider font-bold text-[#FFD60A]">
                                    {t("welcome.dev_link_geen_resend_key_actief")}
                                </p>
                                <a href={devLink} className="text-[11px] text-foreground/90 break-all block mt-1 underline">
                                    {devLink}
                                </a>
                                <button type="button" onClick={() => {
            navigator.clipboard?.writeText(devLink);
            toast.success(t("welcome.link_gekopieerd"));
          }} className="mt-2 text-[10px] flex items-center gap-1 text-[#FFD60A]">
                                    <Copy size={10} /> {t("welcome.kopieer")}
                                </button>
                            </div>}

                        <button type="button" onClick={() => {
          setSent(false);
          setDevLink(null);
        }} data-testid="magic-resend" className="mt-4 text-[12px] text-[#FFD60A] font-semibold">
                            {t("welcome.andere_e_mail_gebruiken")}
                        </button>
                    </div>}

                {/* Demo bypass — non-native or ?demo=1 */}
                {demoVisible && <div className="w-full mt-6 pt-5 border-t border-border/30">
                        <p className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground text-center mb-3">
                            {t("welcome.internal_tester")}
                        </p>
                        <button type="button" onClick={demo} disabled={demoLoading} data-testid="demo-login-btn" className="w-full rounded-2xl py-3 border border-[#FFD60A]/40
                                       text-[#FFD60A] font-semibold text-[13px]
                                       flex items-center justify-center gap-2
                                       active:scale-[0.99] transition disabled:opacity-50" style={{
          background: "rgba(255,214,10,0.05)"
        }}>
                            {demoLoading ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                            {t("welcome.demo_log_in_als_tester")}
                        </button>
                    </div>}

                <p className="text-center text-[11px] text-muted-foreground mt-5 leading-relaxed">
                    {t("welcome.footer")}
                </p>
                {/* Hidden-in-plain-sight diagnostics link — surfaces the
                    iOS WebView error log so frustrated users can share
                    a stack trace without USB-to-Mac Safari Web Inspector.
                    Tap target is small enough not to attract regular
                    user curiosity, but obvious when you're looking. */}
                <button type="button" onClick={() => nav("/diagnostics")} data-testid="welcome-diag-link" className="block mx-auto mt-3 text-[10px] text-muted-foreground/50 underline">
                    {t("welcome.diagnostiek")}
                </button>
            </div>
        </div>;
}